import React, { useState, useEffect, useRef, useMemo } from "react";
import DataTable from "../utils/DataTable";
import api from "../../api";
import { DateTime } from "luxon";
import "./Sales.css";
import SearchBar from "../utils/SearchBar";
import ActionBar from "../utils/ActionBar";
import SalesFilterMenu from "./SalesFilterMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faFilter, faChevronRight, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import RightDetails from "../utils/RightDetails";
import { formatDate } from "../../utils/dateUtils";
import { peopleColumns } from "../people/People"; // Import the columns from People.js

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status) {
    case "Qualified":
      return "#8BC34A"; // Green
    case "New":
      return "#2196F3"; // Blue
    case "Contacted":
      return "#FFC107"; // Amber
    case "Nurturing":
      return "#FF9800"; // Orange
    case "Unqualified":
      return "#F44336"; // Red
    default:
      return "#e0e0e0"; // Grey
  }
};

const getContrastColor = (color) => {
  // Extract RGB components
  let r = 0, g = 0, b = 0;
  
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else if (color.startsWith('rgb')) {
    const rgbValues = color.match(/\d+/g);
    if (rgbValues && rgbValues.length >= 3) {
      r = parseInt(rgbValues[0]);
      g = parseInt(rgbValues[1]);
      b = parseInt(rgbValues[2]);
    }
  }
  
  // Calculate luminance - standard formula for perceived brightness
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return black for bright colors, white for dark colors
  return luminance > 0.5 ? "#000000" : "#ffffff";
};

const Sales = ({ showToast = (msg, type) => console.log(msg, type), currentUser, onDetailPanelChange }) => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [people, setPeople] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filters, setFilters] = useState({
    person_id: "",
    company_id: "",
    status: "",
    searchQuery: "",
    statuses: [],
    assignedTo: [],
    vip: "all",
    hiddenStatuses: []
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [detailsData, setDetailsData] = useState(null);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const tableRef = useRef(null);
  
  // Notify parent when detail panel opens/closes
  useEffect(() => {
    if (onDetailPanelChange) {
      onDetailPanelChange(!!detailsData);
    }
  }, [detailsData, onDetailPanelChange]);

  // Function to fetch and open person details
  const handleViewPerson = async (personId) => {
    try {
      const response = await api.get(`/people/${personId}`);
      setDetailsData({
        ...response.data,
        fromPage: "People", // This ensures the proper PeopleDetails component is used with all inputs
        columns: peopleColumns // Add the columns data needed by PeopleDetails
      });
    } catch (error) {
      console.error("Error fetching person details:", error);
      showToast("Error loading person details", "error");
    }
  };

  // Function to fetch and open company details
  const handleViewCompany = async (companyId) => {
    try {
      const response = await api.get(`/companies/${companyId}`);
      setDetailsData({
        ...response.data,
        fromPage: "Company" // This ensures the proper CompanyDetails component is used
      });
    } catch (error) {
      console.error("Error fetching company details:", error);
      showToast("Error loading company details", "error");
    }
  };

  // Handle view details
  const handleViewDetails = (sale) => {
    setDetailsData({
      ...sale,
      fromPage: "Sales"
    });
  };

  // Define column configuration
  const salesColumns = useMemo(() => [
    {
      accessor: "selection",
      massSelection: true,
      width: 20,
      Header: "",
    },
    {
      accessor: "expandCollapse",
      width: 30,
      Header: "",
    },
    {
      Header: "ID",
      accessor: "id",
      width: 60,
    },
    {
      Header: "Date",
      accessor: "transaction_date",
      width: 150,
      Cell: ({ value }) => {
        if (!value) return "";
        return formatDate(value);
      },
    },
    {
      Header: "Person",
      accessor: "person_name",
      width: 180,
      Cell: ({ row }) => {
        return row.original.name || "N/A";
      },
      buttonOnHover: {
        label: "👤",
        title: "View Person Details",
        onClick: (row) => handleViewPerson(row.person_id),
      },
    },
    {
      Header: "Company",
      accessor: "company_name",
      width: 180,
      Cell: ({ value }) => {
        // Just show the company name, count is now shown by the expand button
        return value || "N/A";
      },
      buttonOnHover: {
        label: "🏢",
        title: "View Company Details",
        onClick: (row) => handleViewCompany(row.company_id),
      },
    },
    {
      Header: "Amount",
      accessor: "total_amount",
      width: 120,
      Cell: ({ value, row }) => {
        if (value === null || value === undefined) return "";
        return `$${parseFloat(value).toFixed(2)}`;
      },
      // Custom function to determine if buttonOnHover should be shown
      // This will be used by DataTable to check if the button should be displayed
      shouldShowButtonOnHover: (row) => !row.original.isGroupRow,
      buttonOnHover: {
        label: "💰",
        title: "View Sale Details",
        onClick: (row) => handleViewDetails(row)
      }
    },
    {
      Header: "Sales Rep",
      accessor: "rep_name",
      width: 180,
      Cell: ({ row }) => {
        console.log("Rendering Sales Rep cell for row:", row.original);
        console.log("Rep data available:", {
          rep_name: row.original.rep_name,
          attributed_to: row.original.attributed_to,
          rep_details: row.original.rep_details
        });
        
        // For rows with a single rep, just use the provided rep_name which should 
        // already include the full name from the backend
        if (row.original.rep_name) {
          return row.original.rep_name;
        }
        
        // If we have attributed_to but no rep_name, use the attributed_to value directly
        // This is a fallback, but backend should ideally provide rep_name
        if (row.original.attributed_to) {
          return `Rep ID: ${row.original.attributed_to}`;
        }
        
        // For rows with multiple reps, use the rep_details array
        if (row.original.rep_details && row.original.rep_details.length > 0) {
          if (row.original.rep_details.length === 1) {
            const rep = row.original.rep_details[0];
            return rep.name || `${rep.first_name || ""} ${rep.last_name || ""}`.trim() || "N/A";
          } else {
            // Show the first rep with a "+X more" indicator
            const rep = row.original.rep_details[0];
            const moreCount = row.original.rep_details.length - 1;
            return (
              <div>
                <div>{rep.name || `${rep.first_name || ""} ${rep.last_name || ""}`.trim()}</div>
                <div className="rep-count">+{moreCount} more</div>
                <div className="rep-tooltip">
                  {row.original.rep_details.map((rep, i) => (
                    <div key={i} className="rep-tooltip-item">
                      {rep.name || `${rep.first_name || ""} ${rep.last_name || ""}`.trim()}
                      {rep.attributed_amount ? ` ($${parseFloat(rep.attributed_amount).toFixed(2)})` : ""}
                    </div>
                  ))}
                </div>
              </div>
            );
          }
        }
        
        return "N/A";
      },
    },
    {
      Header: "Status",
      accessor: "person_status",
      width: 120,
      dropdown: false,
      Cell: ({ value }) => {
        const statusColor = getStatusColor(value);
        return (
          <span
            style={{
              backgroundColor: statusColor,
              color: getContrastColor(statusColor),
              padding: "2px 8px",
              borderRadius: "12px",
              fontSize: "0.85em",
              display: "inline-block"
            }}
          >
            {value || "Unknown"}
          </span>
        );
      }
    },
    {
      Header: "Notes",
      accessor: "notes",
      width: 200,
    },
  ], []);

  // Fetch sales data
  const fetchSales = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      
      if (filters.person_id) queryParams.append("person_id", filters.person_id);
      if (filters.company_id) queryParams.append("company_id", filters.company_id);
      if (filters.status) queryParams.append("status", filters.status);
      
      const response = await api.get(`/sales?${queryParams.toString()}`);
      console.log("Sales API response:", response.data);
      
      // Log the first sale item to inspect its structure
      if (response.data && response.data.length > 0) {
        console.log("First sale object structure:", response.data[0]);
        console.log("Rep properties:", {
          attributed_to: response.data[0].attributed_to,
          rep_name: response.data[0].rep_name,
          rep_first_name: response.data[0].rep_first_name,
          rep_last_name: response.data[0].rep_last_name,
          rep_details: response.data[0].rep_details
        });
      }
      
      setSales(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching sales:", error);
      showToast("Error fetching sales data", "error");
      setLoading(false);
    }
  };

  // Fetch people data for filtering
  const fetchPeople = async () => {
    try {
      const response = await api.get("/people");
      setPeople(response.data);
    } catch (error) {
      console.error("Error fetching people:", error);
    }
  };

  // Fetch companies data for filtering
  const fetchCompanies = async () => {
    try {
      const response = await api.get("/companies");
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchSales();
    fetchPeople();
    fetchCompanies();
  }, []);

  // Filter sales based on criteria
  const filteredSales = useMemo(() => {
    let filtered = sales;
    
    // Filter by search query
    if (filters.searchQuery) {
      const searchLower = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(sale => {
        return (
          (sale.name && sale.name.toLowerCase().includes(searchLower)) ||
          (sale.company_name && sale.company_name.toLowerCase().includes(searchLower)) ||
          (sale.notes && sale.notes.toLowerCase().includes(searchLower)) ||
          (sale.person_status && sale.person_status.toLowerCase().includes(searchLower)) ||
          (sale.id && sale.id.toString().includes(searchLower))
        );
      });
    }
    
    // Filter by statuses
    if (filters.statuses.length > 0) {
      filtered = filtered.filter(sale => filters.statuses.includes(sale.person_status));
    }
    
    // Filter by person ID
    if (filters.person_id) {
      filtered = filtered.filter(sale => sale.person_id === parseInt(filters.person_id));
    }
    
    // Filter by company ID
    if (filters.company_id) {
      filtered = filtered.filter(sale => sale.company_id === parseInt(filters.company_id));
    }
    
    return filtered;
  }, [sales, filters]);

  // Handle cell updates
  const handleCellUpdate = async (id, field, value) => {
    // Skip updates for person_status field as we don't want to modify it directly
    if (field === 'person_status') return;
    
    try {
      await api.put(`/sales/${id}`, { [field]: value });
      showToast("Sale updated successfully", "success");
      
      // Update local state
      setSales((prevSales) =>
        prevSales.map((sale) =>
          sale.id === parseInt(id) ? { ...sale, [field]: value } : sale
        )
      );
    } catch (error) {
      console.error("Error updating sale:", error);
      showToast("Error updating sale", "error");
    }
  };

  // Handle selection change
  const handleSelectionChange = (ids) => {
    setSelectedIds(ids);
  };

  // Handle adding a new sale
  const handleAddSale = () => {
    // Redirect to sales/new or open modal
    console.log("Add new sale");
  };

  return (
    <div className="sales-container">
      <SearchBar
        placeholder="Search sales..."
        onSearch={(query) => setFilters(prev => ({ ...prev, searchQuery: query }))}
      />
      
      <div style={{ position: "relative" }}>
        <ActionBar
          selectedCount={selectedIds.length}
          totalCount={filteredSales.length}
          entityName="sale"
        >
          <button onClick={handleAddSale} title="Add Sale">
            <FontAwesomeIcon icon={faPlus} />
          </button>
          <button onClick={() => setIsFilterMenuOpen(true)} title="Filter">
            <FontAwesomeIcon icon={faFilter} />
          </button>
        </ActionBar>
        
        {isFilterMenuOpen && (
          <div className="menu-overlay">
            <SalesFilterMenu
              filters={filters}
              setFilters={setFilters}
              onClose={() => setIsFilterMenuOpen(false)}
            />
          </div>
        )}
      </div>

      {/* Data Table */}
      <div className="table-container" ref={tableRef}>
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <DataTable
            columns={salesColumns}
            data={filteredSales}
            onCellUpdate={handleCellUpdate}
            onSelectionChange={handleSelectionChange}
            enableRowContextMenu={true}
            groupBy="company_id"
            getRowContextMenuOptions={(row) => [
              {
                label: "View Details",
                action: () => handleViewDetails(row),
              },
              {
                label: "Edit Sale",
                action: () => console.log("Edit", row.id),
              },
              {
                label: "Delete Sale",
                action: () => console.log("Delete", row.id),
                divider: true,
                dangerous: true,
              },
            ]}
          />
        )}
      </div>

      {detailsData && (
        <RightDetails
          data={detailsData}
          fromPage={detailsData.fromPage || "Sales"}
          columns={detailsData.columns}
          onClose={() => setDetailsData(null)}
          onSave={async (result) => {
            try {
              // Check which type of data we're dealing with
              if (result.fromPage === "People") {
                // Refresh people data if needed
                fetchPeople();
              } else if (result.fromPage === "Company") {
                // Refresh companies data if needed
                fetchCompanies();
              } else {
                // It's a sale, refresh sales data
                fetchSales();
              }
              
              // Only close the panel if it's a sale being saved
              if (!result.fromPage || result.fromPage === "Sales") {
                setDetailsData(null);
              }
              
              return true;
            } catch (error) {
              console.error("Error in onSave handler:", error);
              showToast("Error updating data in UI", "error");
              return false;
            }
          }}
          showToast={showToast}
        />
      )}
    </div>
  );
};

export default Sales; 